import { Alert, Snackbar, Stack } from '@mui/material';
import { DataGridPro, GridCellEditStopReasons, GridCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import axios from 'axios';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectSection } from '../../../core/domain/Project';
import apiEndpoint from '../../../core/legacy/utils/api';
import EditProjectSection from '../../../core/project/commands/EditProjectSection';
import FilterProjectCommand from '../../../core/project/commands/FilterProjectCommand';
import { InitProjectCommand } from '../../../core/project/commands/InitProjectCommand';
import LoadColumnsPreferenceCommand from '../../../core/project/commands/LoadColumnsPreferenceCommand';
import SaveColumnsPreferenceCommand from '../../../core/project/commands/SaveColumnsPreferenceCommand';
import { RepairSolution } from '../../../core/project/ExternalProjectPort';
import {
    currentProjectIdQuery,
    loadingProjectSectionsQuery,
    projectSectionsColumnVisibilityQuery,
    projectSectionsFilterQuery,
    projectSectionsPaginationQuery,
    projectSectionsQuery,
} from '../../../core/project/queries';
import { paginationUpdated, setSelectedProjectSections } from '../../../core/project/state';
import localeTexts from '../../../providers/translations/dataGridFilterTranslations';
import { useAppDispatch, useAppSelector } from '../../../types';
import { theme } from '../../legacy/style/theme';
import CustomPagination from '../../session/list-sessions/CustomPagination';
import CustomToolbar from '../../session/list-sessions/CustomToolbar';
import { gridStyle } from '../../style/datagrid';
import { EmptyProjectList } from './EmptyProjectList';
import { getDataGridColumns } from './getDataGridColumns';
import { ProjectListModals } from './Modals';
import { ProjectHeader } from './ProjectHeader';
import { ProjectSnackbarProvider } from './ProjectSnackbarContext';

export const ProjectListPage: FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const projectSections = useAppSelector(projectSectionsQuery);
    const apiRef = useGridApiRef();
    const pagination = useAppSelector(projectSectionsPaginationQuery);
    const loadingState = useAppSelector(loadingProjectSectionsQuery);
    const columnsVisibilityPref = useAppSelector(projectSectionsColumnVisibilityQuery);
    const filter = useAppSelector(projectSectionsFilterQuery);

    const [locallySelectedIds, setLocallySelectedIds] = useState<string[]>([]);
    const [panelElement, setPanelElement] = useState<HTMLElement | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
    const currentProjectId = useAppSelector(currentProjectIdQuery);
    const [repairSolutions, setRepairSolutions] = useState<RepairSolution[] | undefined>(undefined);

    useEffect(() => {
        const loadRepairSolutions = async () => {
            try {
                const response = await axios.request({
                    method: 'GET',
                    url: `${apiEndpoint}/project/repair-solutions`,
                    withCredentials: true,
                });
                setRepairSolutions(response.data);
            } catch (err) {
                console.error('Error loading repair solutions:', err);
                setRepairSolutions([]);
            }
        };
        loadRepairSolutions();
    }, []);

    const columns = useMemo(() => getDataGridColumns(t, true, repairSolutions), [t, repairSolutions]);
    const isLoading = useMemo(() => loadingState || columns.length === 0, [loadingState, columns]);

    useEffect(() => {
        dispatch(InitProjectCommand());
        dispatch(LoadColumnsPreferenceCommand());
    }, [dispatch]);

    useEffect(() => {
        dispatch(InitProjectCommand());
    }, [currentProjectId, dispatch]);

    const handleColumnsVisibilityChange = (newPref: Record<string, boolean>) => {
        dispatch(SaveColumnsPreferenceCommand(newPref));
    };

    const handleRowEdit = useCallback(
        async (updatedRow: ProjectSection, originalRow: ProjectSection) => {
            if (!currentProjectId) {
                setSnackbarMessage(t('projectList.error.noProjectSelected'));

                return originalRow;
            }

            try {
                const updatableFields: (keyof ProjectSection)[] = [
                    'priority',
                    'repairSolution',
                    'conditionalCost',
                    'repairYear',
                    'repairFollowUp',
                    'assignee',
                    'comment',
                ];

                const projectSectionData: Partial<ProjectSection> = {};
                for (const field of updatableFields) {
                    if (updatedRow[field] !== originalRow[field]) {
                        const value = updatedRow[field];
                        const normalizedValue = value === '' ? null : value;
                        if (normalizedValue !== undefined && normalizedValue !== null) {
                            (projectSectionData as Record<string, unknown>)[field] = normalizedValue;
                        } else if (normalizedValue === null) {
                            (projectSectionData as Record<string, unknown>)[field] = null;
                        }
                    }
                }

                if (Object.keys(projectSectionData).length === 0) {
                    return updatedRow;
                }

                await dispatch(
                    EditProjectSection({
                        projectSectionId: updatedRow.id,
                        projectId: currentProjectId,
                        projectSectionData,
                    }),
                ).unwrap();

                return updatedRow;
            } catch (err: unknown) {
                if (
                    axios.isAxiosError(err) &&
                    err.response &&
                    err.response.data &&
                    typeof err.response.data === 'object' &&
                    'message' in err.response.data &&
                    typeof err.response.data.message === 'string'
                ) {
                    setSnackbarMessage(err.response.data.message);
                } else {
                    setSnackbarMessage(t('projectList.error.default'));
                }

                return originalRow;
            }
        },
        [dispatch, currentProjectId, t],
    );

    return (
        <ProjectSnackbarProvider setSnackbarMessage={setSnackbarMessage}>
            <Stack sx={{ width: '98%', height: '100%' }}>
                <ProjectHeader />
                <Stack
                    sx={{
                        height: 'calc(100vh - 226px)',
                        padding: '10px 20px 0',
                        borderRadius: '6px',
                        boxShadow: theme.boxShadow.grey.thin,
                        backgroundColor: theme.color.white.main,
                    }}
                >
                    <DataGridPro
                        columns={columns}
                        apiRef={apiRef}
                        loading={isLoading}
                        rows={projectSections}
                        sx={gridStyle}
                        slots={{
                            noRowsOverlay: EmptyProjectList,
                            toolbar: CustomToolbar,
                        }}
                        slotProps={{
                            panel: { anchorEl: panelElement },
                            toolbar: { setPanelElement },
                        }}
                        localeText={localeTexts}
                        filterModel={filter}
                        onFilterModelChange={(m) => dispatch(FilterProjectCommand(m))}
                        onStateChange={({ filter: { filteredRowsLookup }, rowSelection }) => {
                            const selectedProjectSectionsMatchingFilters =
                                Object.keys(filteredRowsLookup).length > 0
                                    ? rowSelection.filter((id: string) => filteredRowsLookup[id])
                                    : rowSelection;

                            dispatch(setSelectedProjectSections(selectedProjectSectionsMatchingFilters));
                        }}
                        rowSelectionModel={locallySelectedIds}
                        getRowId={(row) => row.id}
                        onRowSelectionModelChange={(m) => setLocallySelectedIds(m as string[])}
                        pagination
                        paginationModel={pagination}
                        onPaginationModelChange={(newPagination) => {
                            if (!isLoading) {
                                dispatch(paginationUpdated(newPagination));
                            }
                        }}
                        pageSizeOptions={[25, 50, 100, 500, 1000]}
                        columnVisibilityModel={columnsVisibilityPref}
                        onColumnVisibilityModelChange={handleColumnsVisibilityChange}
                        processRowUpdate={handleRowEdit}
                        onCellClick={(params: GridCellParams, event) => {
                            if (params.colDef.editable) {
                                event.stopPropagation();
                                try {
                                    apiRef.current.startCellEditMode({ id: params.id, field: params.field });
                                } catch {
                                    // ignore error
                                }
                            }
                        }}
                        onCellEditStop={({ field, id, reason }, event) => {
                            if (reason === GridCellEditStopReasons.escapeKeyDown) {
                                event.defaultMuiPrevented = true;
                                apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
                            }
                        }}
                        checkboxSelection
                        hideFooter
                        hideFooterPagination
                    />
                </Stack>
                <CustomPagination
                    label="tronçon"
                    pluralLabel="tronçons"
                    apiRef={apiRef}
                    onPaginationChange={(newPagination) => {
                        if (!isLoading) {
                            dispatch(paginationUpdated(newPagination));
                        }
                    }}
                />
                <ProjectListModals />
                {snackbarMessage && (
                    <Snackbar
                        open
                        autoHideDuration={3000}
                        onClose={() => setSnackbarMessage(null)}
                        message={snackbarMessage}
                    >
                        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Stack>
        </ProjectSnackbarProvider>
    );
};
